/**
 * Bottom-sheet submenu for tab-bar items that expose multiple destinations
 * (e.g. Priorities, Plans, Engine) without resetting to a section homepage.
 */
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { BottomSheet } from './BottomSheet';
import type { MobileNavItem } from './navConfig';

interface MobileTabSubmenuProps {
  open: boolean;
  onClose: () => void;
  tab: MobileNavItem | null;
}

export function MobileTabSubmenu({ open, onClose, tab }: MobileTabSubmenuProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const go = (href: string) => {
    onClose();
    if (location.pathname !== href) navigate(href);
  };

  if (!tab?.submenu?.length) return null;

  return (
    <BottomSheet open={open} onClose={onClose} title={tab.label}>
      <div className="space-y-1">
        {tab.submenu.map((item) => {
          const Icon = item.icon;
          const isCurrent =
            location.pathname === item.href || location.pathname.startsWith(item.href + '/');
          return (
            <button
              key={item.href + item.label}
              type="button"
              onClick={() => go(item.href)}
              className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-left min-h-[48px] active:bg-gray-50 dark:active:bg-[var(--hover-tint)] ${
                isCurrent
                  ? 'bg-primary-50 dark:bg-primary-500/20 ring-1 ring-primary-200 dark:ring-primary-400/40'
                  : 'bg-white dark:bg-[var(--surface)] border border-gray-100 dark:border-[var(--rule)]'
              }`}
            >
              <span
                className={`h-9 w-9 flex-shrink-0 rounded-lg flex items-center justify-center ${
                  isCurrent
                    ? 'bg-primary-100 text-primary-700 dark:bg-primary-500/30 dark:text-primary-300'
                    : 'bg-gray-100 text-gray-600 dark:bg-[var(--bg)] dark:text-[var(--text-sec)]'
                }`}
              >
                <Icon className="h-[18px] w-[18px]" />
              </span>
              <span className="flex-1 text-[15px] font-medium text-gray-800 dark:text-[var(--text-pri)]">
                {item.label}
              </span>
              <ChevronRight className="h-4 w-4 text-gray-300 dark:text-[var(--text-ter)]" />
            </button>
          );
        })}
      </div>
    </BottomSheet>
  );
}
